import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageTransition from '../components/PageTransition';
import StickyMobileCTA from '../components/StickyMobileCTA';
import { withPublicUrl } from '../utils/publicPath';
import { useConsent } from '../contexts/ConsentContext';

// ── VideoHero ────────────────────────────────────────────────────────────────

const VIDEO_ID = 'yMYF9GMOlP4';

const VideoHero: React.FC = () => {
  const [playing, setPlaying] = useState(false);
  const [pendingPlay, setPendingPlay] = useState(false);
  const { externalMedia, reopen } = useConsent();

  // If the visitor pressed play before consenting, start playback automatically
  // once they allow external media in the banner.
  useEffect(() => {
    if (externalMedia === 'granted' && pendingPlay) {
      setPlaying(true);
      setPendingPlay(false);
    }
  }, [externalMedia, pendingPlay]);

  const handlePlay = () => {
    if (externalMedia === 'granted') {
      setPlaying(true);
    } else {
      setPendingPlay(true);
      reopen();
    }
  };

  return (
    // Outer shell: capped at 90vh so text overlay never leaves the screen on wide monitors
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: 'min(56.25vw, 90vh)',
        overflow: 'hidden',
        backgroundColor: '#000',
      }}
    >
      {/* Video layer — maintains 16:9 and is centered/cropped inside the shell */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          aspectRatio: '16 / 9',
        }}
      >
        {playing ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${VIDEO_ID}?rel=0&modestbranding=1&autoplay=1`}
            title="CarTech Dornbirn Imagefilm"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
          />
        ) : (
          <img
            src={withPublicUrl('/images/general/about-video-poster.jpg')}
            alt="CarTech Dornbirn Imagefilm"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        )}
      </div>

      {/* Scrim — only needed when thumbnail/not playing */}
      {!playing && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 60%)',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Custom play button */}
      {!playing && (
        <button
          onClick={handlePlay}
          aria-label="Video abspielen"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '72px',
            height: '72px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: '2px solid rgba(255,255,255,0.5)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
            transition: 'background 0.2s ease, transform 0.2s ease',
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.25)';
            (e.currentTarget as HTMLButtonElement).style.transform = 'translate(-50%, -50%) scale(1.08)';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.15)';
            (e.currentTarget as HTMLButtonElement).style.transform = 'translate(-50%, -50%) scale(1)';
          }}
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="white" style={{ marginLeft: '3px' }}>
            <path d="M8 5v14l11-7z" />
          </svg>
        </button>
      )}

      {/* Text overlay — always anchored to the visible bottom of the shell */}
      {!playing && (
        <motion.div
          className="about-video-hero-caption"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut', delay: 0.3 }}
          style={{
            position: 'absolute',
            bottom: '28px',
            left: '28px',
            pointerEvents: 'none',
            zIndex: 2,
          }}
        >
          <p
            style={{
              fontSize: '0.75rem',
              letterSpacing: '0.1em',
              color: 'rgba(255,255,255,0.7)',
              textTransform: 'uppercase',
              marginBottom: '8px',
            }}
          >
            — ÜBER UNS
          </p>
          <h1
            style={{
              fontSize: 'clamp(1.5rem, 3.5vw, 2.5rem)',
              fontWeight: 600,
              color: 'white',
              maxWidth: '600px',
              lineHeight: 1.2,
              margin: 0,
              letterSpacing: '-0.01em',
            }}
          >
            Wir reparieren.<br />Damit du sicher unterwegs bist.
          </h1>
        </motion.div>
      )}
    </div>
  );
};

// ── EditorialRow ─────────────────────────────────────────────────────────────

interface EditorialRowProps {
  imageLeft: boolean;
  imageSrc: string;
  imageAlt: string;
  heading: string;
  body: string[];
}

const EditorialRow: React.FC<EditorialRowProps> = ({ imageLeft, imageSrc, imageAlt, heading, body }) => {
  const imageCol = (
    <motion.div
      key="image"
      className="editorial-image-col"
      initial={{ opacity: 0, scale: 1.03 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      style={{ flex: '0 0 55%', maxWidth: '55%' }}
    >
      <img
        src={imageSrc}
        alt={imageAlt}
        loading="lazy"
        style={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', display: 'block' }}
      />
    </motion.div>
  );

  const textCol = (
    <motion.div
      key="text"
      className="editorial-text-col"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      style={{
        flex: '0 0 45%',
        maxWidth: '45%',
        padding: '0 48px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: '40px',
          height: '3px',
          background: 'var(--color-brand)',
          marginBottom: '1rem',
          borderRadius: '2px',
        }}
      />
      <h2
        style={{
          fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
          fontWeight: 600,
          color: 'var(--color-text)',
          marginBottom: '1rem',
          letterSpacing: '-0.02em',
          lineHeight: 1.2,
        }}
      >
        {heading}
      </h2>
      {body.map((paragraph, i) => (
        <p
          key={i}
          style={{
            fontSize: '1rem',
            lineHeight: 1.75,
            color: 'var(--color-text-secondary)',
            marginBottom: i < body.length - 1 ? '1rem' : 0,
          }}
        >
          {paragraph}
        </p>
      ))}
    </motion.div>
  );

  return (
    <div className="editorial-row">
      {imageLeft ? imageCol : textCol}
      {imageLeft ? textCol : imageCol}
    </div>
  );
};

// ── Philosophie ───────────────────────────────────────────────────────────────

const values = [
  { label: 'Preise', tagline: 'Fair und ohne Überraschungen' },
  { label: 'Kommunikation', tagline: 'Klar und transparent' },
  { label: 'Erfahrung', tagline: 'Auf die man sich verlassen kann' },
];

const stripVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
};

// ── About ────────────────────────────────────────────────────────────────────

const About: React.FC = () => (
  <PageTransition>
    <div className="about-page" style={{ backgroundColor: 'var(--color-bg-warm)', overflowX: 'hidden' }}>

      {/* 1 · Cinematic Video Hero */}
      <VideoHero />

      {/* 2 · Geschichte & Team — vertical padding via .about-editorial-section (tighter on mobile) */}
      <section className="about-editorial-section" style={{ backgroundColor: 'var(--color-bg-warm)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

          <EditorialRow
            imageLeft={true}
            imageSrc={withPublicUrl('/images/general/key.jpg')}
            imageAlt="CarTech Werkstatt Innenansicht"
            heading="Unsere Geschichte"
            body={[
              'CarTech Dornbirn wurde 2016 gegründet und hat sich schnell von einem klassischen Handwerksbetrieb zu einem modernen Dienstleister für komplexe Unfallreparaturen entwickelt.',
              'Unser Ziel war es von Anfang an, Qualität und Kundenservice auf ein neues Level zu heben. Durch stetige Investitionen in modernste Technologien und die Weiterbildung unseres Teams konnten wir uns als vertrauenswürdiger Partner in der Region etablieren.',
            ]}
          />

          <div style={{ height: 'clamp(48px, 6vw, 80px)' }} />

          <EditorialRow
            imageLeft={false}
            imageSrc={withPublicUrl('/images/general/team.jpg')}
            imageAlt="Das Team von CarTech Dornbirn"
            heading="Unser Team"
            body={[
              'Hinter CarTech steht ein engagiertes Team aus Experten der Bereiche Karosseriebau, Lackierung und Fahrzeugtechnik.',
              'Jeder Mitarbeiter bringt seine individuelle Expertise ein, um für unsere Kunden das bestmögliche Ergebnis zu erzielen. Wir legen großen Wert auf ein familiäres Betriebsklima und offene Kommunikation.',
            ]}
          />

        </div>
      </section>

      {/* 3 · Philosophie */}
      <section
        className="about-philosophie"
        style={{
          backgroundColor: 'var(--color-dark)',
          padding: 'clamp(64px, 8vw, 100px) 20px',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            style={{
              fontSize: 'clamp(1.5rem, 3.5vw, 2.25rem)',
              fontWeight: 300,
              color: 'white',
              textAlign: 'center',
              maxWidth: '700px',
              margin: '0 auto',
              lineHeight: 1.5,
            }}
          >
            „Wir reparieren, damit du sicher unterwegs bist."
          </motion.p>

          <motion.div
            className="values-strip"
            variants={stripVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            style={{
              marginTop: '64px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'stretch',
            }}
          >
            {values.map((v, i) => (
              <React.Fragment key={v.label}>
                <motion.div
                  className="value-item"
                  variants={itemVariants}
                  style={{ textAlign: 'center', padding: '0 40px', flex: 1, maxWidth: '280px' }}
                >
                  <p
                    style={{
                      fontSize: '0.8rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      color: 'rgba(255,255,255,0.5)',
                      marginBottom: '8px',
                    }}
                  >
                    {v.label}
                  </p>
                  <p style={{ fontSize: '1rem', fontWeight: 500, color: 'white', marginBottom: 0 }}>
                    {v.tagline}
                  </p>
                </motion.div>
                {i < values.length - 1 && (
                  <div
                    className="value-divider"
                    style={{ width: '1px', background: 'rgba(255,255,255,0.15)', alignSelf: 'stretch' }}
                  />
                )}
              </React.Fragment>
            ))}
          </motion.div>

          <motion.div
            className="cta-desktop-only"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.4, ease: 'easeOut', delay: 0.4 }}
            style={{ textAlign: 'center', marginTop: '48px' }}
          >
            <Link
              to="/appointment"
              style={{
                display: 'inline-block',
                padding: '14px 40px',
                backgroundColor: 'var(--color-brand)',
                color: 'white',
                fontWeight: 600,
                fontSize: '0.95rem',
                letterSpacing: '0.03em',
                textDecoration: 'none',
                borderRadius: '4px',
              }}
            >
              Termin vereinbaren
            </Link>
          </motion.div>

        </div>
      </section>

    </div>

    <StickyMobileCTA />
  </PageTransition>
);

export default About;
